/**
 * SHAPES LAB — Code101 chapter 3, where the same facts have completely different futures.
 *
 * FOUNDER, 2026-08-14: "ch3 next".
 *
 * ── THE CHAPTER NAMES ITS OWN LOAD-BEARING SECTION ────────────────────────────────────────────
 * §5 opens: "This is the load-bearing section of today. Re-read it twice." So this lab is that
 * section and nothing else. It hands over the whole design — a paragraph, a table, the same five
 * facts, and a list of questions each with the operation it needs written in brackets.
 *
 * ── WHY BOTH PANES ANSWER AT ONCE ─────────────────────────────────────────────────────────────
 * A learner clicks one question and BOTH shapes try. The table answers with its working shown; the
 * paragraph refuses, in the module's own words. Asking them one at a time would let the refusal
 * read as a broken feature. Side by side, with the same question, it reads as what it is: the same
 * five facts, and only one of them can be asked.
 *
 * ── WHAT IT REFUSES ───────────────────────────────────────────────────────────────────────────
 * The paragraph pane can find a word. That is all, because that is all the module grants it: "a
 * machine can search for the word 'petrol' — that is about it." Parsing the numbers out of the
 * prose would have been easy and would have taught the opposite of the section — that the shape
 * does not really matter. The refusal is the teaching.
 *
 * It also does not let a learner edit the table into new columns. The temptation is obvious and
 * the section closes on why it would be a different lesson: "Get the shape right at the start."
 * Re-shaping data after capture is chapter 3's sequel, not chapter 3.
 */

import { useState } from 'react';
import { Value } from '../../../packages/ankr-glass/src/react.jsx';
import { computed, refused, cited } from '../../../packages/ankr-provenance/src/provenance.mjs';
import {
  PARAGRAPH, ROWS, QUESTIONS, SOURCE, askTable, searchParagraph,
  WHY_PARAGRAPH_CANNOT, PARAGRAPH_CAN, NO_AI_NEEDED, BINS, THE_DECISION, EVERY_FIELD,
} from './shapes.mjs';

export default function ShapesLab() {
  const [qid, setQid] = useState(null);
  const [word, setWord] = useState('');

  const answer = qid ? askTable(qid) : null;
  const q = QUESTIONS.find((x) => x.id === qid);
  const hit = word.trim() ? searchParagraph(word) : null;

  return (
    <div className="g-glass shell-panel lab">
      <div className="g-label">Shapes lab · the same five facts</div>
      <h2 className="g-h2 lab-h">Same facts. Completely different futures.</h2>
      <p className="g-body">
        Below are the same five expenses written twice — once as a note you might actually write,
        once as a small table. Pick a question and ask <b>both</b>. One of them answers in
        milliseconds. The other cannot answer at all, and it is not because it is missing anything.
      </p>

      {/* ── THE QUESTIONS ── */}
      <div className="lab-task-head">
        <span className="g-label">Ask</span>
        {QUESTIONS.map((x) => (
          <button key={x.id} className={`g-btn ${qid === x.id ? 'g-btn-primary' : ''}`}
            onClick={() => setQid(x.id)}>{x.ask}</button>
        ))}
      </div>

      <div className="shp-two">
        {/* ── SHAPE A ── */}
        <div className="shp-pane">
          <div className="g-label">Shape A · a paragraph in a notebook</div>
          <p className="shp-para">{PARAGRAPH}</p>

          <div className="shp-ans">
            {!qid && <span className="g-body g-dim">Pick a question above.</span>}
            {qid && (
              <>
                <div className="g-label">{q.ask}</div>
                {/* REFUSED, not "failed" and not empty. The engine's own vocabulary: this is a
                    thing the system cannot do and can say why, which is different from an outage
                    and different from a missing value. */}
                <Value of={refused(WHY_PARAGRAPH_CANNOT)} />
              </>
            )}
          </div>

          <div className="shp-search">
            <div className="g-label">What it CAN do</div>
            <p className="g-body g-dim"><Value of={cited(PARAGRAPH_CAN, SOURCE)} /></p>
            <input className="morse-input shp-input" value={word} placeholder="find a word — try petrol"
              onChange={(e) => setWord(e.target.value)} />
            {hit && (
              <p className="g-body">
                {hit.found
                  ? <><b>Found</b> “{hit.word}”. That is the whole capability.</>
                  : <>Not in the text. Still the whole capability.</>}
              </p>
            )}
          </div>
        </div>

        {/* ── SHAPE B ── */}
        <div className="shp-pane">
          <div className="g-label">Shape B · a tiny table</div>
          <table className="shp-table">
            <thead><tr><th>Day</th><th>Category</th><th>Amount</th></tr></thead>
            <tbody>
              {ROWS.map((r, i) => {
                /* The rows the operation actually touched, lit. A learner watching "sum where
                   Day = Mon" should see WHICH rows were summed, or the answer is just a number
                   that appeared — which is the same as being told, and being told is the text. */
                const used = qid === 'monday' ? r.day === 'Mon'
                  : qid === 'biggest' ? r.amount === Math.max(...ROWS.map((x) => x.amount))
                  : qid === 'median' ? r.amount === 240
                  : qid === 'largestday' ? r.day === 'Wed'
                  : qid === 'smallestcat' ? r.category === 'Tea'
                  : Boolean(qid);
                return (
                  <tr key={i} className={qid && used ? 'shp-used' : (qid ? 'shp-unused' : '')}>
                    <td>{r.day}</td><td>{r.category}</td><td className="shp-num">{r.amount}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className="shp-ans">
            {!qid && <span className="g-body g-dim">Pick a question above.</span>}
            {qid && answer && (
              <>
                <div className="g-label">{q.ask}</div>
                <div className="shp-big"><Value of={computed(answer.value)} /></div>
                {/* The WORKING, in his bracket. The number without the operation is a conclusion;
                    with it, it is a machine a learner can follow. */}
                <p className="g-body g-dim shp-op"><Value of={cited(answer.op, SOURCE)} /></p>
                {answer.note && <p className="g-body shp-note">{answer.note}</p>}
              </>
            )}
          </div>
        </div>
      </div>

      <p className="g-body shp-punch"><Value of={cited(NO_AI_NEEDED, SOURCE)} /></p>

      <div className="lab-task">
        <div className="g-label">Why this is the load-bearing section</div>
        <p className="g-body"><Value of={cited(THE_DECISION, SOURCE)} /></p>
        <p className="g-body g-dim"><Value of={cited(EVERY_FIELD, SOURCE)} /></p>
        <div className="lab-readout" style={{ marginTop: 'var(--g-2)' }}>
          <div><span className="g-label">Shape A is</span><Value of={cited(BINS.unstructured, BINS.source)} /></div>
          <div><span className="g-label">Shape B is</span><Value of={cited(BINS.structured, BINS.source)} /></div>
        </div>
      </div>

      <p className="g-body g-dim lab-why">
        The paragraph is not broken and the table is not clever. They hold the same five facts. The
        table can be asked because someone decided, before writing anything down, what the columns
        would be. This lab does not let you add a column — re-shaping data after it is captured is
        a real and separate job, and the section's advice is to not need it:
        <b> “Get the shape right at the start.”</b>
      </p>
    </div>
  );
}
